import { Message } from "@ai-sdk/ui-utils";

/**
 * Options for creating a copilot tool message
 */
interface CopilotToolMessageOptions {
  isError: boolean;
  errorMessage?: string;
  toolArgs?: Record<string, unknown>;
  successMessage?: string;
  errorPrompt?: string;
  toolName?: string;
}

/**
 * Utility function to create messages with tool calls for AI toolkit
 * Used for copilot interactions after tool operations
 *
 * This is a pure utility function that can be used both in React hooks
 * and in Jotai atom setters.
 */
export const buildInformedAssistantMessage = (
  options: CopilotToolMessageOptions,
): Message[] => {
  const {
    isError,
    errorMessage,
    toolArgs = {
      displayMessage: "Building the system...",
    },
    successMessage = "Successfully completed the operation",
    errorPrompt = "Looks like there is bad data on the canvas, want me to fix that for you?",
    toolName = "build_system",
  } = options;

  if (isError && !errorMessage) {
    throw new Error("Error message is required when error is present");
  }
  const messageText = isError ? errorPrompt : successMessage;

  const resultText = isError
    ? `Failed due to an error in your canvas`
    : successMessage;

  return [
    {
      role: "assistant",
      content: messageText,
      id: `${toolName}-${isError ? "error" : "success"}-${Date.now()}`,
      createdAt: new Date(),
      parts: [
        {
          type: "tool-invocation",
          toolInvocation: {
            state: "result",
            toolName,
            toolCallId: `${toolName}-${Date.now()}`,
            args: toolArgs,
            result: isError
              ? {
                  $error: {
                    message: errorMessage || "Unknown error",
                  },
                }
              : {
                  content: [
                    {
                      type: "text",
                      text: resultText,
                    },
                  ],
                },
          },
        },
        { type: "text" as const, text: messageText },
      ],
    },
  ];
};
